import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react'
import { fetchBlogPosts } from '../utils/api'
import type { BlogPost } from '../types'

export function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchBlogPosts()
      .then(setPosts)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="flex-1 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Blog: Resolução de Contratos de Seguro
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Artigos e guias práticos sobre como resolver contratos de seguro em Portugal. 
            Informação útil sobre prazos, direitos e procedimentos.
          </p>
        </div>

        {posts.length === 0 ? (
          <div className="card p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nenhum artigo disponível de momento.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="card p-6 block hover:shadow-lg transition-shadow group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
                      {post.category}
                    </span>
                    <h2 className="text-xl font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4">{post.excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(post.created_at).toLocaleDateString('pt-PT')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.read_time} leitura
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0 mt-2" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-12 bg-primary-50 rounded-2xl p-8 text-center">
          <BookOpen className="w-12 h-12 text-primary-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Precisa de resolver um contrato de seguro?
          </h2>
          <p className="text-gray-600 mb-4">
            Use a nossa ferramenta gratuita para gerar a minuta de resolução automaticamente.
          </p>
          <Link to="/" className="btn-primary inline-flex">
            Gerar Minuta de Resolução
          </Link>
        </div>
      </div>
    </div>
  )
}
